import React from 'react';

import { Box, Button, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

export type ImageCropMode = 'fixed' | 'freeform';

export interface ImageAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
  warmth: number;
}

interface Props {
  adjustmentRange?: number;
  adjustments: ImageAdjustments;
  cropMode: ImageCropMode;
  disabled?: boolean;
  fixedAspectRatio: number;
  onAdjustmentsChange: (nextAdjustments: ImageAdjustments) => void;
  onCropModeChange: (nextCropMode: ImageCropMode) => void;
  onReset: () => void;
}

const adjustmentControls: Array<{
  key: keyof ImageAdjustments;
  label: string;
}> = [
  { key: 'brightness', label: 'Brightness' },
  { key: 'contrast', label: 'Contrast' },
  { key: 'saturation', label: 'Saturation' },
  { key: 'warmth', label: 'Warmth' },
];

function getFixedCropLabel(fixedAspectRatio: number) {
  return fixedAspectRatio >= 1
    ? `Fixed ${Math.round(fixedAspectRatio * 9)}:9`
    : `Fixed 9:${Math.round(9 / fixedAspectRatio)}`;
}

function snapSliderValue(value: number) {
  return Math.abs(value) <= 4 ? 0 : value;
}

export default function ImageAdjustmentControls({
  adjustmentRange = 100,
  adjustments,
  cropMode,
  disabled = false,
  fixedAspectRatio,
  onAdjustmentsChange,
  onCropModeChange,
  onReset,
}: Props) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <Stack spacing={1}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontWeight: 600 }} variant="caption">
            Crop mode
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={cropMode}
            onChange={(_, nextValue: ImageCropMode | null) => {
              if (nextValue) {
                onCropModeChange(nextValue);
              }
            }}
          >
            <ToggleButton disabled={disabled} value="fixed">
              {getFixedCropLabel(fixedAspectRatio)}
            </ToggleButton>
            <ToggleButton disabled={disabled} value="freeform">
              Freeform
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {adjustmentControls.map((control) => (
          <Box key={control.key}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
              <Typography sx={{ fontWeight: 600 }} variant="caption">
                {control.label}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {adjustments[control.key] > 0 ? '+' : ''}
                {adjustments[control.key]}
              </Typography>
            </Stack>

            <Slider
              disabled={disabled}
              max={adjustmentRange}
              min={-adjustmentRange}
              size="small"
              step={1}
              sx={{
                mt: -0.25,
                mb: -0.5,
              }}
              value={adjustments[control.key]}
              onChange={(_, value) => {
                if (Array.isArray(value)) {
                  return;
                }

                onAdjustmentsChange({
                  ...adjustments,
                  [control.key]: snapSliderValue(value),
                });
              }}
            />
          </Box>
        ))}

        <Stack alignItems="center" direction="row" justifyContent="flex-end">
          <Button disabled={disabled} size="small" sx={{ minWidth: 0, px: 0.5 }} variant="text" onClick={onReset}>
            Reset
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
